import {
  FE_KNOWN_ROUTES_LISTING,
  FE_ROUTE_MANIFEST,
  type FeNavigationCaller,
  type FeRouteEntry,
} from '@/routes/route-manifest'

import type { ExecuteClientToolDeps } from './client-tool-types'

/** Longest requested path echoed back verbatim, mirroring the backend manifest's cap on injection surface. */
const MAX_ECHOED_ROUTE_LENGTH = 80

/**
 * Segment-wise match against `FE_ROUTE_MANIFEST`, a verbatim mirror of the
 * backend `matchRoute` (`services/console/ai/src/routes/routeManifest.ts`):
 * exact literal first so a literal entry can never be shadowed by a `:param`
 * one, then equal segment counts with each manifest segment either equal to
 * the input segment or a non-empty `:param` position.
 */
function matchFeRoute(path: string): FeRouteEntry | undefined {
  const exact = FE_ROUTE_MANIFEST.find(e => e.route === path)
  if (exact) return exact

  const pathSegments = path.split('/')

  for (const entry of FE_ROUTE_MANIFEST) {
    const entrySegments = entry.route.split('/')
    if (entrySegments.length !== pathSegments.length) continue
    if (entrySegments.every((seg, i) => seg === pathSegments[i] || (seg.startsWith(':') && pathSegments[i] !== ''))) {
      return entry
    }
  }

  return undefined
}

/**
 * The FE half of `resolveNavigationTarget` — same four refusals, same wording,
 * so the reason the model relays to the user reads the same whichever side
 * produced it.
 *
 * Defaults to the LEAST privileged caller when none is supplied: an unknown
 * caller must not be treated as entitled.
 */
export function resolveFeNavigationTarget(
  route: string,
  caller: FeNavigationCaller,
): { ok: true; route: string } | { ok: false; reason: string } {
  const entry = matchFeRoute(route)

  if (!entry) {
    const shown = route.length > MAX_ECHOED_ROUTE_LENGTH ? `${route.slice(0, MAX_ECHOED_ROUTE_LENGTH)}…` : route

    return {
      ok: false,
      reason: `"${shown}" isn't a known page, so I can't navigate there. Known routes:\n${FE_KNOWN_ROUTES_LISTING}`,
    }
  }

  // Kept BYTE-FOR-BYTE identical to the backend's `resolveNavigationTarget`
  // — an empty-string tenantId is NOT a central operator here. `useAuth`
  // carries a central operator as `''`, and normalising that to `null` is the
  // ADAPTER's job (`AssistantProvider`, one place), not a second dialect in
  // the resolver. `resolveNavigationTarget.parity.spec.ts` runs both
  // resolvers over one shared case table and pins this.
  const isCentralOperator = caller.tenantId === null || caller.tenantId === undefined

  if (entry.centralOnly && !isCentralOperator) {
    return { ok: false, reason: `${entry.label} is only available to the central operator account.` }
  }

  if (entry.requiredModule && !caller.assignedModules.includes(entry.requiredModule)) {
    return {
      ok: false,
      reason: `Your account doesn't have access to ${entry.label} — the "${entry.requiredModule}" module isn't enabled for your organization.`,
    }
  }

  if (entry.requiredRole && caller.role !== entry.requiredRole) {
    return {
      ok: false,
      reason: `Your role ("${caller.role}") doesn't have access to ${entry.label} — it requires the "${entry.requiredRole}" role.`,
    }
  }

  return { ok: true, route }
}

export const LEAST_PRIVILEGED_CALLER: FeNavigationCaller = { role: 'viewer', tenantId: 'unknown', assignedModules: [] }

/** Poll cadence + ceiling for the default settle. */
const SETTLE_POLL_MS = 10
const SETTLE_DEADLINE_MS = 200

const delay = (ms: number) => new Promise<void>(resolve => setTimeout(resolve, ms))

/**
 * Wait until `currentRoute()` reports something other than `from`, or the
 * deadline elapses. Resolves either way — the caller then reports whatever the
 * route reads, which is the honest answer in both cases.
 */
async function settleUntilRouteChanges(currentRoute: () => string, from: string): Promise<void> {
  const deadline = Date.now() + SETTLE_DEADLINE_MS

  while (Date.now() < deadline) {
    if (currentRoute() !== from) return
    await delay(SETTLE_POLL_MS)
  }
}

export async function executeNavigateTo(input: unknown, deps: ExecuteClientToolDeps): Promise<unknown> {
  const raw = (input ?? {}) as { route?: unknown; discardUnsaved?: unknown }
  const route = typeof raw.route === 'string' ? raw.route.trim() : ''

  if (!route) {
    return { error: 'navigate_to needs a "route" string, e.g. "/map".' }
  }

  const resolved = resolveFeNavigationTarget(route, deps.caller ?? LEAST_PRIVILEGED_CALLER)

  if (!resolved.ok) {
    return { refused: resolved.reason }
  }

  // `discardUnsaved` is D3's "confirm destructive" living in the TOOL
  // SIGNATURE — negotiated in the conversation, where the user can see the
  // question and the assistant can see the answer — instead of a browser
  // modal the assistant is blind to.
  if (raw.discardUnsaved !== true) {
    const unsaved = deps.hasUnsavedWork()

    if (unsaved) {
      return { blocked_unsaved_work: unsaved }
    }
  }

  const before = deps.currentRoute()

  deps.navigate(resolved.route)

  if (deps.settle) {
    await deps.settle()
  } else {
    await settleUntilRouteChanges(deps.currentRoute, before)
  }

  // Report the route ACTUALLY reached — a guard may have bounced the
  // navigation elsewhere, and claiming the requested route regardless would
  // be exactly the dishonesty this phase exists to remove.
  return { arrived: deps.currentRoute() }
}
